import React from 'react';
import { ModalV2, ModalTitle, MultiToggle } from '../../../../../core/components';
import simulatorConfig, { gridSize } from '../../../constants/simulator-config-constants';
import { simulatorNavDropdownActions } from '../../../constants/simulator-nav-constants';
import { ExperimentConfiguration, IAnatomicalModel } from '../../../models';
import anatomicalModelFeaturesConstants from '../../../constants/anatomical-model-constants';
import { getSelectedValueFromSimulatorConfig } from '../../../utils/get-selected-value-from-simulator-config';
import { isAnatomicalModelFeatureEnabled } from '../../../utils/is-anatomical-model-feature-enabled';
import ButtonToggle from './button-toggle';
import { ModalSectionContainer } from '../modal-section-container';
import { ModalSubSection } from '../modal-sub-section';
import { NoiseStrengthSection } from './noise-strength-section';

export interface ModalProps {
  config: ExperimentConfiguration[];
  anatomicalModel: IAnatomicalModel;
  onClose: VoidFunction;
  setConfigParam: (action: string, value: string, configurationName: string) => void;
}

export const SimulationSettingsModal = ({
  config,
  anatomicalModel,
  onClose,
  setConfigParam,
}: ModalProps): React.ReactElement => {
  const onIsotropicGridSelectHandler = (action: string, grid: string) => {
    setConfigParam(action, grid, simulatorConfig.ISOTROPIC_GRID);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onToggleConfParam = (configurationName: string, _confKey: string) => {
    const currentVal = config?.find((c: ExperimentConfiguration) => c.name === configurationName)?.selectedValue;
    const newVal = currentVal === '0' ? '1' : '0';

    // [PT-63] - fix for react-bootstrap Formcheck not toggling as expected
    setTimeout(() => {
      setConfigParam('true', newVal, configurationName);
    }, 50);
  };

  const defaultConfigurationSettingsSelected =
    getSelectedValueFromSimulatorConfig(config, simulatorConfig.OVERWRITE_SIMULATION_SETTINGS) === '1';

  const isNoiseChecked = getSelectedValueFromSimulatorConfig(config, simulatorConfig.NOISE) === '1';

  const isSimulateSliceThicknessChecked =
    getSelectedValueFromSimulatorConfig(config, simulatorConfig.PERFORMANCE_GRIDZ_SLICE_THICKNESS) === '1';

  const showPhysiologicalMotion = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.physiologicalMotion
  );
  const isPhysiologicalMotionChecked =
    getSelectedValueFromSimulatorConfig(config, simulatorConfig.PHYSIOLOGICAL_MOTION) === '1';

  const showActivateCS = isAnatomicalModelFeatureEnabled(anatomicalModel, anatomicalModelFeaturesConstants.CS);
  const isActivateCSChecked = getSelectedValueFromSimulatorConfig(config, simulatorConfig.ACTIVE_CS) === '1';

  const showActivateSusceptibility = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.SWI
  );
  const isActivateSusceptibilityChecked =
    getSelectedValueFromSimulatorConfig(config, simulatorConfig.SUSCEPTIBILITY) === '1';

  const showT2Star = isAnatomicalModelFeatureEnabled(anatomicalModel, anatomicalModelFeaturesConstants.t2_star);
  const isT2StarChecked = getSelectedValueFromSimulatorConfig(config, simulatorConfig.T2STAR) === '1';

  const gridType = getSelectedValueFromSimulatorConfig(config, simulatorConfig.ISOTROPIC_GRID);
  const gridSizeOption = getSelectedValueFromSimulatorConfig(config, simulatorConfig.GRID_SIZE_OPTION);
  const getSelectedSimulatorModelResolution = () => {
    if (gridType === '0') return simulatorNavDropdownActions.set_isotropic_0;
    if (gridSizeOption === gridSize.SIZE_1_MM) return simulatorNavDropdownActions.set_isotropic_1;
    if (gridSizeOption === gridSize.SIZE_0_5_MM) return simulatorNavDropdownActions.set_isotropic_05;
    if (gridSizeOption === gridSize.SIZE_OPTIMAL) return simulatorNavDropdownActions.set_isotropic_optimal;
    return '';
  };

  const isFastAlgorithmChecked = getSelectedValueFromSimulatorConfig(config, simulatorConfig.FAST_ALGORITHM) === '1';

  const showFuzzyMode = isAnatomicalModelFeatureEnabled(anatomicalModel, anatomicalModelFeaturesConstants.fuzzyMode);
  const isFuzzyModeChecked = getSelectedValueFromSimulatorConfig(config, simulatorConfig.FUZZY_MODE) === '1';

  return (
    <ModalV2
      closeOnOutsideClick
      withCloseButton
      onClose={onClose}
      header={<ModalTitle>Simulation settings</ModalTitle>}
      style={{ width: '1024px' }}
    >
      <>
        <ModalSectionContainer title="Basic">
          <div className="col-12 settings-modal__control-wrapper mt-3">
            <ButtonToggle
              className="me-2 mb-2"
              checked={isSimulateSliceThicknessChecked}
              disabled={defaultConfigurationSettingsSelected}
              id="performance_gridZ_sliceThickness"
              onClick={() =>
                onToggleConfParam(simulatorConfig.PERFORMANCE_GRIDZ_SLICE_THICKNESS, 'performance_gridZ_sliceThickness')
              }
            >
              Simulate slice thickness
            </ButtonToggle>

            {showPhysiologicalMotion && (
              <ButtonToggle
                className="me-2 mb-2"
                checked={isPhysiologicalMotionChecked}
                disabled={defaultConfigurationSettingsSelected}
                id="physiologicalMotion"
                onClick={() => onToggleConfParam(simulatorConfig.PHYSIOLOGICAL_MOTION, 'physiologicalMotion')}
              >
                Simulate physiological motion
              </ButtonToggle>
            )}
          </div>

          <div className="col-12 settings-modal__control-wrapper mt-0">
            {showActivateCS && (
              <ButtonToggle
                className="me-2 mb-2"
                checked={isActivateCSChecked}
                disabled={defaultConfigurationSettingsSelected}
                id="activate_cs"
                onClick={() => onToggleConfParam(simulatorConfig.ACTIVE_CS, 'activate_cs')}
              >
                Activate Chemical Shift
              </ButtonToggle>
            )}

            {showActivateSusceptibility && (
              <ButtonToggle
                className="me-2 mb-2"
                checked={isActivateSusceptibilityChecked}
                disabled={defaultConfigurationSettingsSelected}
                id="susceptibility"
                onClick={() => onToggleConfParam(simulatorConfig.SUSCEPTIBILITY, 'susceptibility')}
              >
                Activate susceptibility
              </ButtonToggle>
            )}

            {showT2Star && (
              <ButtonToggle
                className="me-2 mb-2"
                checked={isT2StarChecked}
                disabled={defaultConfigurationSettingsSelected}
                id="t2Star"
                onClick={() => onToggleConfParam(simulatorConfig.T2STAR, 't2Star')}
              >
                Activate T2* (beta)
              </ButtonToggle>
            )}
          </div>
        </ModalSectionContainer>
        <NoiseStrengthSection config={config} setNoiseStrength={setConfigParam} />
        <ModalSectionContainer title="Advanced">
          <ModalSubSection title="Simulator model resolution">
            <MultiToggle
              items={[
                { id: simulatorNavDropdownActions.set_isotropic_0, label: 'Model native' },
                { id: simulatorNavDropdownActions.set_isotropic_1, label: '1.0mm isotropic' },
                { id: simulatorNavDropdownActions.set_isotropic_05, label: '0.5mm isotropic' },
                { id: simulatorNavDropdownActions.set_isotropic_optimal, label: 'Simulator optimal' },
              ]}
              onSelect={(selectedSimulatorModelResolution) =>
                !defaultConfigurationSettingsSelected &&
                onIsotropicGridSelectHandler(
                  selectedSimulatorModelResolution,
                  selectedSimulatorModelResolution === simulatorNavDropdownActions.set_isotropic_0 ? '0' : '1'
                )
              }
              selectedItemKey={getSelectedSimulatorModelResolution()}
            />

            <div className="col-12 settings-modal__control-wrapper mt-0">
              <ButtonToggle
                checked={isFastAlgorithmChecked}
                disabled={defaultConfigurationSettingsSelected}
                id="fast_algorithm"
                onClick={() => onToggleConfParam(simulatorConfig.FAST_ALGORITHM, 'fast_algorithm')}
              >
                Fast algorithm
              </ButtonToggle>
            </div>
          </ModalSubSection>

          {showFuzzyMode && (
            <div className="col-12 settings-modal__control-wrapper mt-0">
              <ButtonToggle
                checked={isFuzzyModeChecked}
                id="fuzzyMode"
                onClick={() => onToggleConfParam(simulatorConfig.FUZZY_MODE, 'fuzzyMode')}
              >
                Fuzzy model
              </ButtonToggle>
            </div>
          )}
        </ModalSectionContainer>
      </>
    </ModalV2>
  );
};
